import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Loader2, HeartHandshake } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  referrer_name: "",
  referrer_email: "",
  referee_name: "",
  referee_email: "",
  referee_phone: "",
  relationship: "",
  message: "",
};

export default function ReferFamily() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [count, setCount] = useState(0);

  const set = (k) => (v) =>
    setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v?.target?.value ?? "" }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.referrer_name.trim() || !form.referrer_email.trim() || !form.referee_name.trim()) {
      toast.error("Please share your name, your email, and the family you’re referring.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/referrals`,
        {
          referrer_name: form.referrer_name,
          referrer_email: form.referrer_email,
          referee_name: form.referee_name,
          referee_email: form.referee_email || null,
          referee_phone: form.referee_phone,
          relationship: form.relationship,
          message: form.message,
        },
        { timeout: 20000 }
      );
      setCount((c) => c + 1);
      setForm((f) => ({
        ...initial,
        referrer_name: f.referrer_name,
        referrer_email: f.referrer_email,
      }));
      toast.success("Thank you. We’ll reach out with discretion.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send the referral. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="refer"
      data-testid="refer-family-section"
      className="relative bg-[var(--bone-soft)] text-[var(--ink)]"
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-16 max-w-[1400px] py-24 md:py-36 lg:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left */}
          <div className="lg:col-span-5 lg:sticky lg:top-28 self-start reveal">
            <div className="text-overline mb-6" style={{ color: "#E87A4C", letterSpacing: "0.22em" }}>
              Refer a family
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.05] tracking-tight">
              Know another family who’d <span className="italic text-[var(--gold)]">belong here?</span>
            </h2>
            <p className="mt-7 text-[var(--ink)]/72 text-base md:text-lg leading-relaxed font-light max-w-md">
              Our best partnerships come from quiet introductions. Share a name in confidence and
              we’ll reach out personally — with care, and only if it’s the right fit. They’ll
              never know it came from you unless you’d like them to.
            </p>

            <div className="mt-10 flex items-start gap-4">
              <HeartHandshake className="h-6 w-6 text-[var(--gold)] mt-1 shrink-0" strokeWidth={1.6} />
              <p className="text-sm text-[var(--ink)]/70 font-light leading-relaxed max-w-md">
                Already a Co‑Own owner? Refer as many families as you like — this page is
                always here for you.
              </p>
            </div>
          </div>

          {/* Right - Form */}
          <form
            data-testid="refer-family-form"
            onSubmit={onSubmit}
            className="lg:col-span-7 bg-[var(--bone)] border border-[var(--border-soft)] p-7 md:p-12 reveal"
          >
            {count > 0 && (
              <div
                data-testid="refer-family-thanks"
                className="mb-7 px-4 py-3 bg-[var(--bone-soft)] border border-[var(--border-soft)] text-sm text-[var(--ink)]/80 flex items-start gap-3"
              >
                <CheckCircle2 className="h-4 w-4 text-[var(--gold)] mt-0.5 shrink-0" strokeWidth={2} />
                <span>
                  {count === 1
                    ? "Referral received — thank you. Feel free to add another below."
                    : `${count} referrals received — thank you. Add another below.`}
                </span>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <div className="text-overline text-[var(--ink)]/45 mb-4">About you</div>
              </div>
              <Field label="Your name" required>
                <Input
                  data-testid="refer-input-your-name"
                  value={form.referrer_name}
                  onChange={set("referrer_name")}
                  placeholder="Daniel Cohen"
                  className="h-12 rounded-none bg-white border-[var(--border-soft)] focus-visible:ring-1 focus-visible:ring-[var(--ink)]"
                />
              </Field>
              <Field label="Your email" required>
                <Input
                  data-testid="refer-input-your-email"
                  type="email"
                  value={form.referrer_email}
                  onChange={set("referrer_email")}
                  placeholder="daniel@example.com"
                  className="h-12 rounded-none bg-white border-[var(--border-soft)] focus-visible:ring-1 focus-visible:ring-[var(--ink)]"
                />
              </Field>

              <div className="md:col-span-2 mt-4">
                <div className="text-overline text-[var(--ink)]/45 mb-4">About them</div>
              </div>
              <Field label="Their family / name" required>
                <Input
                  data-testid="refer-input-name"
                  value={form.referee_name}
                  onChange={set("referee_name")}
                  placeholder="e.g. The Levy family"
                  className="h-12 rounded-none bg-white border-[var(--border-soft)] focus-visible:ring-1 focus-visible:ring-[var(--ink)]"
                />
              </Field>
              <Field label="Their email">
                <Input
                  data-testid="refer-input-email"
                  type="email"
                  value={form.referee_email}
                  onChange={set("referee_email")}
                  placeholder="levy@example.com"
                  className="h-12 rounded-none bg-white border-[var(--border-soft)] focus-visible:ring-1 focus-visible:ring-[var(--ink)]"
                />
              </Field>
              <Field label="Their phone">
                <Input
                  data-testid="refer-input-phone"
                  value={form.referee_phone}
                  onChange={set("referee_phone")}
                  placeholder="+61 4xx xxx xxx"
                  className="h-12 rounded-none bg-white border-[var(--border-soft)] focus-visible:ring-1 focus-visible:ring-[var(--ink)]"
                />
              </Field>
              <Field label="How do you know them?">
                <Input
                  data-testid="refer-input-relationship"
                  value={form.relationship}
                  onChange={set("relationship")}
                  placeholder="Cousins · shul friends · etc."
                  className="h-12 rounded-none bg-white border-[var(--border-soft)] focus-visible:ring-1 focus-visible:ring-[var(--ink)]"
                />
              </Field>
              <Field label="A short note (optional)" full>
                <Textarea
                  data-testid="refer-input-message"
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Anything we should know — preferred city, family stage, when to reach out, whether to mention you…"
                  rows={3}
                  className="rounded-none bg-white border-[var(--border-soft)] focus-visible:ring-1 focus-visible:ring-[var(--ink)]"
                />
              </Field>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
              <p className="text-xs text-[var(--ink)]/55 max-w-md leading-relaxed">
                Held in strict confidence. We’ll reach out personally — and only if it’s the right fit.{" "}
                <a
                  href="/privacy"
                  data-testid="refer-privacy-link"
                  className="underline decoration-[var(--ink)]/40 underline-offset-2 hover:text-[var(--ink)] hover:decoration-[var(--ink)] transition-colors"
                >
                  Privacy notice
                </a>
                .
              </p>
              <Button
                data-testid="refer-submit-btn"
                type="submit"
                disabled={submitting}
                className="rounded-full h-12 md:h-14 px-7 md:px-10 bg-[var(--ink)] text-[var(--bone)] hover:bg-[var(--ink-hover)] text-sm md:text-base font-medium tracking-wide transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending…
                  </>
                ) : (
                  "Share this introduction"
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, required, full }) {
  return (
    <div className={`flex flex-col gap-2 ${full ? "md:col-span-2" : ""}`}>
      <Label className="text-overline text-[var(--ink)]/60">
        {label} {required && <span className="text-[var(--terracotta)]">*</span>}
      </Label>
      {children}
    </div>
  );
}
